import { useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { Layout, Menu, Badge, Button, Drawer, Dropdown, type MenuProps } from 'antd';
import { ShoppingCartOutlined, HomeOutlined, UserOutlined, HistoryOutlined, LogoutOutlined } from '@ant-design/icons';
import { useCart, CartSummary } from '@digiwedge/ui-axiz';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { ThemeToggle } from '@digiwedge/ui-antd';
import './ShopLayout.css';

const { Header, Content, Footer } = Layout;

export function ShopLayout() {
  const { items } = useCart();
  const [cartOpen, setCartOpen] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = useGlobalStore((s: GlobalState) => s.isAuthenticated);
  const logout = useGlobalStore((s: GlobalState) => s.logout);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const handleCheckout = () => {
    setCartOpen(false);
    navigate('/checkout');
  };

  return (
    <Layout className="shop-layout">
      <Header className="shop-header">
        <div className="shop-logo">
          <Link to="/">
            <img src="/assets/yolo-logo-small.png" alt="YOLO Shop" height="40" />
          </Link>
        </div>
        <Menu
          theme="dark"
          mode="horizontal"
          selectedKeys={[]}
          items={[
            {
              key: 'home',
              icon: <HomeOutlined />,
              label: <Link to="/">Shop</Link>,
            },
          ]}
          className="shop-menu"
        />
        <div className="shop-actions">
          <ThemeToggle className="theme-toggle" />
          <Badge count={itemCount} size="small">
            <Button
              type="text"
              icon={<ShoppingCartOutlined style={{ fontSize: 20, color: '#fff' }} />}
              onClick={() => setCartOpen(true)}
              aria-label="Open cart"
            />
          </Badge>
          {isAuthenticated ? (
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'orders',
                    icon: <HistoryOutlined />,
                    label: 'My Orders',
                    onClick: () => navigate('/orders'),
                  },
                  { type: 'divider' },
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: 'Logout',
                    onClick: logout,
                  },
                ] as MenuProps['items'],
              }}
              placement="bottomRight"
            >
              <Button type="text" icon={<UserOutlined />} style={{ color: '#fff' }}>
                Account
              </Button>
            </Dropdown>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <Link to="/login">
                <Button type="text" icon={<UserOutlined />} style={{ color: '#fff' }}>
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button type="default" size="small">
                  Register
                </Button>
              </Link>
            </div>
          )}
        </div>
      </Header>
      <Content className="shop-content">
        <Outlet />
      </Content>
      <Footer className="shop-footer">YOLO Shop &copy; {new Date().getFullYear()}</Footer>

      <Drawer
        title="Shopping Cart"
        placement="right"
        onClose={() => setCartOpen(false)}
        open={cartOpen}
        styles={{ wrapper: { width: 400 } }}
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => setCartOpen(false)}>Continue Shopping</Button>
            <Button type="primary" onClick={handleCheckout} disabled={items.length === 0}>
              Checkout
            </Button>
          </div>
        }
      >
        <CartSummary />
      </Drawer>
    </Layout>
  );
}

export default ShopLayout;
